import { PaymentTestModeBanner } from '@/components/PaymentTestModeBanner';
import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { MaintenanceBlocker } from "@/components/MaintenanceRedirect";
import { OnboardingProvider } from "@/contexts/OnboardingContext";
import { OnboardingTour } from "@/components/onboarding/OnboardingTour";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import Index from "./pages/Index";
import { LoadingAnimation } from "@/components/LoadingAnimation";

const AppLayout = lazyWithRetry(() => import("@/components/AppLayout").then(m => ({ default: m.AppLayout })));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const AuthCallback = lazyWithRetry(() => import("./pages/AuthCallback"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Templates = lazyWithRetry(() => import("./pages/Templates"));
const Papers = lazyWithRetry(() => import("./pages/Papers"));
const Favorites = lazyWithRetry(() => import("./pages/Favorites"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const Discover = lazyWithRetry(() => import("./pages/Discover"));
const AdminSettings = lazyWithRetry(() => import("./pages/AdminSettings"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const DebugLogs = lazyWithRetry(() => import("./pages/DebugLogs"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Refund = lazyWithRetry(() => import("./pages/Refund"));
const About = lazyWithRetry(() => import("./pages/About"));
const Guides = lazyWithRetry(() => import("./pages/Guides"));
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const ReferenceStyles = lazyWithRetry(() => import("./pages/ReferenceStyles"));
const OAuthConsent = lazyWithRetry(() => import("./pages/OAuthConsent"));

const queryClient = new QueryClient();

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <LoadingAnimation />
  </div>
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageFallback />;
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return (
    <Suspense fallback={<PageFallback />}>
      <AppLayout>{children}</AppLayout>
    </Suspense>
  );
}

function MaybeAppLayout({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading || !user) return <>{children}</>;
  return (
    <Suspense fallback={<PageFallback />}>
      <AppLayout>{children}</AppLayout>
    </Suspense>
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/dashboard" element={
          <ProtectedRoute><Dashboard /></ProtectedRoute>
        } />
        <Route path="/templates" element={
          <ProtectedRoute><Templates /></ProtectedRoute>
        } />
        <Route path="/papers" element={
          <ProtectedRoute><Papers /></ProtectedRoute>
        } />
        <Route path="/favourites" element={
          <ProtectedRoute><Favorites /></ProtectedRoute>
        } />
        <Route path="/discover" element={
          <ProtectedRoute><Discover /></ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute><Settings /></ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute><AdminSettings /></ProtectedRoute>
        } />
        <Route path="/debug-logs" element={
          <ProtectedRoute><DebugLogs /></ProtectedRoute>
        } />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/refund" element={<Refund />} />
        <Route path="/about" element={<About />} />
        <Route path="/guides" element={<MaybeAppLayout><Guides /></MaybeAppLayout>} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/reference-styles" element={<ReferenceStyles />} />
        <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

// MaintenanceBlocker is at the VERY TOP - before QueryClient, before ThemeProvider, before everything.
// When maintenance is active, NOTHING else renders.
const App = () => (
  <MaintenanceBlocker>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <AuthProvider>
          <SubscriptionProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <PaymentTestModeBanner />
              <BrowserRouter>
                <OnboardingProvider>
                  <AppRoutes />
                  <OnboardingTour />
                </OnboardingProvider>
              </BrowserRouter>
            </TooltipProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </MaintenanceBlocker>
);

export default App;
